import type { FC } from "react";
import { createContext, useCallback, useEffect, useMemo, useState } from "react";

type DrawerContext = {
  isOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
};

type DrawerContextProps = {
  onCancelCallback?: () => void;
  children: React.ReactNode;
};

const DRAWER_BODY_CLASSNAME_ACTIVE_STATUS = "drawerActive";

export const DrawerContext = createContext<DrawerContext | null>(null);

export const DrawerProvider: FC<DrawerContextProps> = ({ onCancelCallback, children }) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const openDrawer = () => {
    setIsOpen(true);
  };

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
    onCancelCallback?.();
  }, [onCancelCallback]);

  const subscribeToHotkeys = useCallback(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Space has semantic meaning for form controls (toggling checkboxes/radios,
      // typing in inputs), so it must not close the drawer when one of them is focused.
      const target = event.target as HTMLElement | null;
      if (event.key === " " && target?.closest("input, textarea, select, [contenteditable='true']")) {
        return;
      }

      if (event.key === "Escape" || event.key === " ") {
        if (!isOpen) return;

        closeDrawer();
        event.preventDefault();
      }
    };

    document.addEventListener("keydown", handleKeyDown, false);

    return {
      unsubscribeFromHotkeys: () => document.removeEventListener("keydown", handleKeyDown, false),
    };
  }, [closeDrawer, isOpen]);

  useEffect(() => {
    document.body.classList.toggle(DRAWER_BODY_CLASSNAME_ACTIVE_STATUS, isOpen);

    return () => {
      document.body.classList.remove(DRAWER_BODY_CLASSNAME_ACTIVE_STATUS);
    };
  }, [isOpen]);

  useEffect(() => {
    const { unsubscribeFromHotkeys } = subscribeToHotkeys();

    return () => unsubscribeFromHotkeys();
  }, [isOpen, subscribeToHotkeys]);

  const context = useMemo(
    () => ({
      isOpen,
      openDrawer,
      closeDrawer,
    }),
    [isOpen]
  );

  return <DrawerContext.Provider value={context}>{children}</DrawerContext.Provider>;
};
